<script setup lang="ts">
import { type UploadFileInfo, type UploadCustomRequestOptions, NIcon } from 'naive-ui';
import { computed, ref } from 'vue';
import { IconUpload } from '@/components/design/icons';
import { useFileUpload } from '@/composables/useFileUpload';

interface Props {
  /** The contract the file is uploaded straight into, as a new contract file. */
  contractUid: string;
  /**
   * Main instruction text displayed in the upload area
   * @default "Click or drag file to this area to upload"
   */
  mainText?: string;
  /**
   * Secondary/helper text displayed below the main text
   * @default "Support for drag & drop uploads"
   */
  helperText?: string;
  /**
   * Whether to allow multiple file selection
   * @default false
   */
  multiple?: boolean;
  /**
   * Accepted file types (e.g., ".pdf,.docx,.doc,.txt")
   * @default ".pdf,.docx,.doc,.txt"
   */
  accept?: string;
  /**
   * Whether to show upload progress
   * @default true
   */
  showProgress?: boolean;
  /**
   * Progress text displayed during upload
   * @default "Uploading file..."
   */
  progressText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  mainText: 'Click or drag file to this area to upload',
  helperText: 'Support for drag & drop uploads',
  multiple: false,
  accept: '.pdf,.docx,.doc,.txt',
  showProgress: true,
  progressText: 'Uploading file...',
});

// File upload composable
const {
  uploadFile: uploadFileToVault,
  isUploading,
  progress,
  error: uploadError,
} = useFileUpload();

// Local state
const uploadFile = ref<UploadFileInfo | null>(null);
const errors = ref<string[]>([]);

// Computed
const hasFile = computed<boolean>(() => uploadFile.value !== null);

// Methods
function handleUploadChange(options: { fileList: UploadFileInfo[] }): void {
  uploadFile.value = options.fileList.at(-1) ?? null;
}

function handleRemove(): void {
  uploadFile.value = null;
}

function handleCustomUpload({ onFinish }: UploadCustomRequestOptions): void {
  onFinish();
}

/**
 * Upload the selected file into the contract
 * @returns Promise that resolves with the uploaded vault file UID
 */
async function uploadSelectedFile(): Promise<string> {
  if (!uploadFile.value || !uploadFile.value.file) {
    throw new Error('No file selected');
  }

  const file = uploadFile.value.file;
  errors.value = [];

  try {
    const result = await uploadFileToVault(file, { contractUid: props.contractUid });

    return result.fileUid;
  } catch (error) {
    const errorMsg =
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      error instanceof Error ? error.message : uploadError.value || 'Unknown upload error';
    errors.value.push(`Error uploading file: ${errorMsg}`);
    throw error;
  }
}

/**
 * Clear the selected file
 */
function clearSelection(): void {
  uploadFile.value = null;
  errors.value = [];
}

// Expose methods for parent components
defineExpose({
  uploadSelectedFile,
  clearSelection,
  hasFile,
});
</script>

<template>
  <div class="contract-file-upload">
    <NUpload
      :multiple="multiple"
      :default-upload="false"
      :custom-request="handleCustomUpload"
      :show-file-list="true"
      :accept="accept"
      @change="handleUploadChange"
      @remove="handleRemove"
    >
      <NUploadDragger>
        <component :is="NIcon" size="48" :depth="3">
          <component :is="IconUpload" />
        </component>
        <div style="padding: 20px">
          <NText style="font-size: 16px">{{ mainText }}</NText>
          <NP depth="3" style="margin: 8px 0 0 0">{{ helperText }}</NP>
        </div>
      </NUploadDragger>
    </NUpload>

    <div v-if="showProgress && isUploading" class="progress-container">
      <NProgress type="line" :percentage="progress" />
      <div class="progress-text">{{ progressText }}</div>
    </div>

    <div v-if="errors.length > 0" class="error-list">
      <NAlert v-for="(error, index) in errors" :key="index" type="error" :title="error" />
    </div>
  </div>
</template>

<style scoped>
.contract-file-upload {
  width: 100%;
}

:deep(.n-upload-dragger) {
  width: 100%;
}

.progress-container {
  margin-top: 16px;
}

.progress-text {
  font-size: 0.8rem;
  color: #666;
  margin-top: 4px;
  text-align: center;
}

.error-list {
  margin-top: 16px;
}
</style>
